<template>
  <v-card>
    <v-sheet class="pa-3 blue darken-1">
      <v-text-field
          v-model="search"
          label="Search Files"
          dark
          flat
          solo-inverted
          hide-details
          clearable
          clear-icon="mdi-close-circle-outline"
          color="grey darken-3"
          prepend-icon="fas fa-search"
        >
        </v-text-field>
      </v-flex>
      <v-fab-transition>
        <v-btn v-show="true" :loading="loading" type="file" color="primary" fab dark absolute bottom right @click="$refs.fileInput.click()">
          <v-icon>add</v-icon>
          <input ref="fileInput" type="file" @change="upload" accept=".csv" style="display: none !important;"></div>
        </v-btn>
      </v-fab-transition>
    </v-sheet>
    <v-layout row wrap justify-space-between pa-3 style="min-height: 490px;">
      <v-flex xs12 md5 class="overflow-x-scroll">
        <v-treeview
          v-model="tree"
          item-key="id"
          item-text="name"
          :items="items"
          return-object
          :search="search"
          :filter="filter"
          :open.sync="open"
          open-on-click
          :active.sync="active"
          activatable
          active-class="blue--text text--darken-1"
          :load-children="fetchFiles"
          transition
        >
          <template v-slot:prepend="{ item, open, active }">
            <v-icon v-if="item.type === 'dir'">
              {{ open ? 'mdi-folder-open' : 'mdi-folder' }}
            </v-icon>
            <v-icon v-else :color="active ? 'blue' : ''">
              {{ fileIcons[item.ext || 'file'] }}
            </v-icon>
          </template>
          <template v-slot:label="{ item, open, active }">
            <span>{{ item.name }}</span>
            <v-btn v-if="item.type === 'dir'" :loading="loading" :disabled="loading" flat icon color="grey darken-1" @click.stop="() => refreshDirectory(item)">
              <v-icon class="pa-0">cached</v-icon>
            </v-btn>
          </template>
        </v-treeview>
      </v-flex>
      <v-flex d-flex text-xs-center class="overflow-x-scroll">
        <v-scroll-y-transition mode="out-in">
          <div v-if="!selected" class="title grey--text text--lighten-1 font-weight-light" style="align-self: center;">
            Select a File
          </div>
          <v-card v-else :key="selected.id" class="mx-auto" flat max-width="400">
            <v-card-text>
              <v-icon size="88" class="mb-2">
                {{ fileIcons[selected.ext || 'file'] }}
              </v-icon>
              <h3 class="headline mb-2"> {{ selected.name }} </h3>
              <div class="mb-2 subheading font-weight-bold">
                <span class="font-weight-bold">Updated: </span>
                <TimeAgo contentClass="blue--text" :date="new Date(selected.updated)" cursor="help"></TimeAgo>
              </div>
              <div>
                <v-btn :disabled="loading" color="success" @click="download">
                  <v-icon left dark>cloud_download</v-icon>
                  Download
                </v-btn>
                <v-btn :disabled="loading" color="error" @click="deleteFile">
                  <v-icon left dark>delete_forever</v-icon>
                  Remove
                </v-btn>
              </div>
            </v-card-text>
            <v-divider></v-divider>
            <v-layout tag="v-card-text" text-xs-left wrap>
              <v-flex tag="strong" xs5 text-xs-right mr-3 mb-2>Name:</v-flex>
              <v-flex>{{ selected.name }}</v-flex>
              <v-flex tag="strong" xs5 text-xs-right mr-3 mb-2>Path:</v-flex>
              <v-flex>{{ selected.path }}</v-flex>
              <v-flex tag="strong" xs5 text-xs-right mr-3 mb-2>Bucket:</v-flex>
              <v-flex>{{ selected.bucket }}</v-flex>
              <v-flex tag="strong" xs5 text-xs-right mr-3 mb-2>Size:</v-flex>
              <v-flex>{{ selected.size }}</v-flex>
              <v-flex tag="strong" xs5 text-xs-right mr-3 mb-2>Created:</v-flex>
              <v-flex>{{ getDateString(selected.created) }}</v-flex>
              <v-flex tag="strong" xs5 text-xs-right mr-3 mb-2>Updated:</v-flex>
              <v-flex>{{ getDateString(selected.updated) }}</v-flex>
            </v-layout>
          </v-card>
        </v-scroll-y-transition>
      </v-flex>
    </v-layout>
  </v-card>
</template>

<script>
import MongoService from '../../services/mongo.service'
import Alert from '../../libs/alert'
import { getDateString } from '../../libs/utils'
import TimeAgo from '../../components/TimeAgo'
const GCS_KEY = 'GOOGLE_CLOUD_STORAGE_RIPPLE_REPORT'

export default {
  components: { TimeAgo },
  data () {
    const buckets = ['ripple-17148a472-gsr']
    return {
      loading: false,
      buckets,
      search: null,
      active: [],
      open: [buckets[0]],
      tree: [],
      files: [],
      fileIcons: {
        file: 'mdi-file-document',
        html: 'mdi-language-html5',
        js: 'mdi-nodejs',
        json: 'mdi-json',
        md: 'mdi-markdown',
        pdf: 'mdi-file-pdf',
        png: 'mdi-file-image',
        txt: 'mdi-file-document-outline',
        xls: 'mdi-file-excel',
        csv: 'fas fa-file-csv'
      }
    }
  },
  computed: {
    items () {
      return [{
        id: this.buckets[0],
        name: this.buckets[0],
        bucket: this.buckets[0],
        type: 'dir',
        path: '',
        children: this.files
      }]
    },
    selected () {
      if (!this.active.length) return undefined
      return this.active[0]
    },
    filter () {
      return undefined
    }
  },
  methods: {
    getDateString (date) {
      return getDateString(date)
    },
    async upload () {
      this.loading = true
      try {
        const file = this.$refs.fileInput.files[0]
        if (file) {
          function getBase64 (f) {
            return new Promise((resolve, reject) => {
              const reader = new FileReader()
              reader.readAsDataURL(f)
              reader.onload = () => resolve(reader.result)
              reader.onerror = error => reject(error)
            })
          }
          const base64 = await getBase64(file)
          await MongoService.googleCloudStorage.upload({
            gcsKey: GCS_KEY,
            bucket: this.buckets[0],
            destination: file.name,
            base64
          })
          this.$refs.fileInput.value = ''
          await this.fetchFiles(this.items[0])
        }
      } catch (e) {
        Alert.error({ message: e.toString() })
      }
      this.loading = false
    },
    async fetchFiles (item) {
      this.loading = true
      try {
        this.files = await MongoService.googleCloudStorage.list({ gcsKey: GCS_KEY, bucket: item.bucket, directory: item.path })
      } catch (e) {
        Alert.error({ message: e.toString() })
      }
      this.loading = false
    },
    async download () {
      this.loading = true
      try {
        if (this.selected) {
          await MongoService.googleCloudStorage.download({ gcsKey: GCS_KEY, bucket: this.selected.bucket, filepath: this.selected.path })
        }
      } catch (e) {
        Alert.error({ message: e.toString() })
      }
      this.loading = false
    },
    async deleteFile () {
      this.loading = true
      try {
        if (this.selected) {
          if (prompt(`Please enter the file name to remove. (${this.selected.name})`) === this.selected.name) {
            await MongoService.googleCloudStorage.delete({ gcsKey: GCS_KEY, bucket: this.selected.bucket, filepath: this.selected.path })
            await this.fetchFiles(this.items[0])
            this.active = []
          } else {
            alert('File name is incorrect')
          }
        }
      } catch (e) {
        Alert.error({ message: e.toString() })
      }
      this.loading = false
    },
    async refreshDirectory (item) {
      await this.fetchFiles(item)
    }
  }
}
</script>

<style lang="scss" scoped>
.overflow-x-scroll {
  overflow-x: scroll;
  overflow-y: visible;
}
</style>

